// A quiet, entirely synthesized pond. Sound is opt-in; no files or external services are needed.
// Positions use stereo pan (-1 left, 0 centre, 1 right). Existing no-argument calls still work.

type Voice = { source: AudioScheduledSourceNode; gain: GainNode; nodes: AudioNode[] };
const between = (low: number, high: number) => low + Math.random() * (high - low);

export class PondSound {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private recording: MediaStreamAudioDestinationNode | null = null;
  private beds: AudioScheduledSourceNode[] = [];
  private bedNodes: AudioNode[] = [];
  private voices = new Set<Voice>();
  private noise: AudioBuffer | null = null;
  private muted = true;
  private level = 0.35;
  private paused = false;
  private scenePaused = false;
  private unavailable = false;
  private listening = false;
  private birdTimer = 0;
  private suspendTimer = 0;
  private playbackEpoch = 0;
  private nextSplash = 0;
  private visibilityChanged = () => this.reconcile();

  constructor() {
    try {
      // Honour an earlier explicit sound choice, including the original mute preference.
      this.muted = localStorage.getItem('lilypad.muted') !== '0';
      const stored = localStorage.getItem('lilypad.volume');
      if (stored !== null && Number.isFinite(Number(stored))) this.level = Math.max(0, Math.min(1, Number(stored)));
    } catch { /* Browser privacy settings must not prevent the pond from opening. */ }
  }

  get isMuted(): boolean { return this.muted; }
  get volume(): number { return this.level; }
  get isPaused(): boolean { return this.paused; }
  get isScenePaused(): boolean { return this.scenePaused; }
  get isAvailable(): boolean {
    return !this.unavailable && typeof window !== 'undefined' && !!this.contextClass();
  }

  private contextClass(): typeof AudioContext | undefined {
    return window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  }

  /** Safe on ordinary page clicks: a new visitor stays silent until they enable sound. */
  start(): void {
    if (this.muted || this.paused || this.scenePaused || this.level === 0 || document.hidden || !this.isAvailable) return;
    if (!this.listening) {
      document.addEventListener('visibilitychange', this.visibilityChanged);
      this.listening = true;
    }
    if (!this.ctx) {
      try {
        const Ctx = this.contextClass()!;
        this.ctx = new Ctx();
        this.master = this.ctx.createGain();
        this.master.gain.value = 0;
        this.master.connect(this.ctx.destination);
        this.makeBeds();
      } catch {
        this.destroy();
        this.unavailable = true;
        return;
      }
    }
    this.reconcile();
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    this.remember('lilypad.muted', this.muted ? '1' : '0');
    if (!this.muted) this.start();
    this.reconcile();
    return this.muted;
  }

  setVolume(value: number): void {
    if (!Number.isFinite(value)) return;
    this.level = Math.max(0, Math.min(1, value));
    this.remember('lilypad.volume', String(this.level));
    if (this.level > 0 && !this.muted) this.start();
    this.reconcile();
  }

  /** A scene pause is separate from the visitor's saved sound preference. */
  setPaused(value: boolean): void {
    this.paused = value;
    if (!value && !this.muted) this.start();
    this.reconcile();
  }

  /** Animation pause must not overwrite a separate choice to pause only the sound. */
  setScenePaused(value: boolean): void {
    this.scenePaused = value;
    this.reconcile();
  }

  /** Tap the existing mix for an operator preview recording. Never starts or unmutes audio. */
  captureStream(): MediaStream | null {
    const ctx = this.ctx;
    if (!ctx || !this.master || typeof ctx.createMediaStreamDestination !== 'function') return null;
    if (!this.recording) {
      try {
        this.recording = ctx.createMediaStreamDestination();
        this.master.connect(this.recording);
      } catch { return null; }
    }
    return this.recording.stream;
  }

  private remember(key: string, value: string): void {
    try { localStorage.setItem(key, value); } catch { /* The controls still work without storage. */ }
  }

  private get wantsPlayback(): boolean {
    return !this.muted && !this.paused && !this.scenePaused && !document.hidden && this.level > 0;
  }

  private reconcile(): void {
    window.clearTimeout(this.birdTimer);
    window.clearTimeout(this.suspendTimer);
    this.birdTimer = this.suspendTimer = 0;
    const ctx = this.ctx, master = this.master, epoch = ++this.playbackEpoch;
    if (!ctx || !master || ctx.state === 'closed') return;
    const fade = (value: number) => {
      master.gain.cancelScheduledValues(ctx.currentTime);
      master.gain.setTargetAtTime(value, ctx.currentTime, 0.045);
    };
    if (this.wantsPlayback) {
      // A browser may refuse resume until a genuine gesture; retry on the next start().
      void ctx.resume().then(() => {
        if (this.ctx !== ctx || epoch !== this.playbackEpoch || !this.wantsPlayback) return;
        fade(this.level * 0.8);
        this.scheduleBird();
      }).catch(() => {});
    } else {
      fade(0);
      this.stopVoices();
      this.suspendTimer = window.setTimeout(() => {
        this.suspendTimer = 0;
        if (this.ctx === ctx && !this.wantsPlayback) void ctx.suspend().catch(() => {});
      }, 180);
    }
  }

  private noiseBuffer(): AudioBuffer {
    if (this.noise) return this.noise;
    const ctx = this.ctx!;
    const buffer = ctx.createBuffer(1, ctx.sampleRate * 4, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      // A very short seam taper keeps the loop free of clicks.
      const edge = Math.min(1, i / 256, (data.length - 1 - i) / 256);
      data[i] = between(-1, 1) * edge;
    }
    this.noise = buffer;
    return buffer;
  }

  private makeBeds(): void {
    const ctx = this.ctx!;
    const bed = (frequency: number, gain: number, swell: number, rate: number, pan: number) => {
      const source = ctx.createBufferSource();
      source.buffer = this.noiseBuffer(); source.loop = true;
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass'; filter.frequency.value = frequency; filter.Q.value = 0.4;
      const envelope = ctx.createGain(); envelope.gain.value = gain;
      const lfo = ctx.createOscillator(); lfo.frequency.value = rate;
      const depth = ctx.createGain(); depth.gain.value = swell;
      const nodes: AudioNode[] = [source, filter, envelope, lfo, depth];
      lfo.connect(depth).connect(envelope.gain);
      source.connect(filter).connect(envelope);
      this.output(envelope, pan, nodes);
      source.start(0, between(0, 3)); lfo.start();
      this.beds.push(source, lfo); this.bedNodes.push(...nodes);
    };
    bed(620, 0.12, 0.035, 0.075, -0.2); // soft water, a slow swell
    bed(1500, 0.025, 0.018, 0.043, 0.35); // light breeze through the bank plants
  }

  private output(node: AudioNode, pan: number, nodes: AudioNode[]): void {
    const ctx = this.ctx!;
    if (typeof ctx.createStereoPanner === 'function') {
      const panner = ctx.createStereoPanner(); panner.pan.value = Math.max(-1, Math.min(1, pan));
      node.connect(panner).connect(this.master!); nodes.push(panner);
    } else node.connect(this.master!);
  }

  private track(source: AudioScheduledSourceNode, gain: GainNode, nodes: AudioNode[]): void {
    const voice = { source, gain, nodes };
    this.voices.add(voice);
    source.onended = () => {
      this.voices.delete(voice);
      nodes.forEach((node) => node.disconnect());
    };
  }

  private get canPlayEffect(): boolean {
    return this.wantsPlayback && this.ctx?.state === 'running' && !!this.master && this.voices.size < 24;
  }

  private tone(frequency: number, duration: number, volume: number, end: number, pan: number, delay = 0,
    type: OscillatorType = 'sine', resonance?: number): void {
    if (!this.canPlayEffect) return;
    const ctx = this.ctx!, at = ctx.currentTime + delay;
    const source = ctx.createOscillator(); source.type = type;
    source.frequency.setValueAtTime(frequency, at);
    source.frequency.exponentialRampToValueAtTime(end, at + duration);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(volume, at + Math.min(0.025, duration / 4));
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    const nodes: AudioNode[] = [source, gain];
    if (resonance) {
      const filter = ctx.createBiquadFilter(); filter.type = 'bandpass'; filter.frequency.value = resonance; filter.Q.value = 2;
      source.connect(filter).connect(gain); nodes.push(filter);
    } else source.connect(gain);
    this.output(gain, pan, nodes); this.track(source, gain, nodes);
    source.start(at); source.stop(at + duration + 0.03);
  }

  bird(pan = between(-0.85, 0.85)): void {
    const base = between(1750, 2500);
    for (let i = 0; i < 3; i++) this.tone(base * (i === 1 ? 1.14 : 1), 0.15, 0.045, base * 1.35, pan, i * 0.17);
  }

  croak(pan = between(-0.65, 0.65)): void {
    for (let i = 0; i < 2; i++) this.tone(between(95, 110), 0.23, 0.16, 72, pan, i * 0.25, 'sawtooth', 430);
  }

  duck(pan = between(-0.55, 0.55)): void {
    for (let i = 0; i < 2; i++) this.tone(i ? 290 : 330, 0.18, i ? 0.10 : 0.14, 225, pan, i * 0.24, 'sawtooth', 950);
  }

  drip(pan = between(-0.65, 0.65)): void {
    this.tone(between(1100, 1500), 0.22, 0.07, 420, pan);
  }

  splash(pan = between(-0.5, 0.5)): void {
    if (!this.canPlayEffect || this.ctx!.currentTime < this.nextSplash) return;
    const ctx = this.ctx!, at = ctx.currentTime;
    this.nextSplash = at + 0.15;
    const source = ctx.createBufferSource(); source.buffer = this.noiseBuffer();
    const filter = ctx.createBiquadFilter(); filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2800, at); filter.frequency.exponentialRampToValueAtTime(450, at + 0.5);
    const gain = ctx.createGain(); gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(0.15, at + 0.025); gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.5);
    const nodes: AudioNode[] = [source, filter, gain];
    source.connect(filter).connect(gain); this.output(gain, pan, nodes); this.track(source, gain, nodes);
    source.start(at); source.stop(at + 0.55);
    this.tone(380, 0.28, 0.045, 125, pan);
  }

  private scheduleBird(): void {
    if (this.birdTimer || !this.wantsPlayback || this.ctx?.state !== 'running') return;
    this.birdTimer = window.setTimeout(() => {
      this.birdTimer = 0;
      if (!this.wantsPlayback || this.ctx?.state !== 'running') return;
      if (this.canPlayEffect) this.bird();
      this.scheduleBird();
    }, between(9000, 22000));
  }

  private stopVoices(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    for (const voice of this.voices) {
      voice.gain.gain.cancelScheduledValues(ctx.currentTime);
      voice.gain.gain.setTargetAtTime(0, ctx.currentTime, 0.015);
      try { voice.source.stop(ctx.currentTime + 0.07); } catch { /* Already ended. */ }
    }
  }

  /** Release every timer, source and browser listener. A later gesture may start a fresh context. */
  destroy(): void {
    ++this.playbackEpoch;
    window.clearTimeout(this.birdTimer); window.clearTimeout(this.suspendTimer);
    this.birdTimer = this.suspendTimer = 0;
    document.removeEventListener('visibilitychange', this.visibilityChanged); this.listening = false;
    for (const voice of this.voices) {
      voice.source.onended = null;
      try { voice.source.stop(); } catch { /* Already ended. */ }
      voice.nodes.forEach((node) => node.disconnect());
    }
    this.voices.clear();
    for (const source of this.beds) { try { source.stop(); } catch { /* Already stopped. */ } }
    this.bedNodes.forEach((node) => node.disconnect());
    this.beds = []; this.bedNodes = []; this.noise = null; this.nextSplash = 0;
    this.recording?.stream.getTracks().forEach((track) => track.stop());
    this.recording?.disconnect(); this.recording = null;
    this.master?.disconnect(); this.master = null;
    const ctx = this.ctx; this.ctx = null;
    if (ctx && ctx.state !== 'closed') void ctx.close().catch(() => {});
  }
}

export const sound = new PondSound();
